// src/components/ecommerce/BookingSidebar.jsx - MATCH CARTSIDEBAR EXACTLY

import React, { useState, useRef, useEffect } from 'react';
import {
  X,
  Calendar,
  Clock,
  MapPin,
  Trash2,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  MessageSquare,
  Mail,
  Info,
  ShoppingBag,
  CreditCard,
  ArrowRight,
  Shield,
  Truck,
  Package
} from 'lucide-react';
import { CheckoutModal } from './CheckoutModal';

export const BookingSidebar = ({ 
  isOpen, 
  onClose, 
  bookings = [],
  onRemoveBooking,
  onClearBookings,
  onContact,
  storeSettings = {},
  styling = {} 
}) => {
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false); // ✅ ADD CHECKOUT MODAL STATE
  const [selectedDeliveryZone, setSelectedDeliveryZone] = useState(null);
  const [showDeliveryError, setShowDeliveryError] = useState(false);

  const isOnlinePaymentEnabled = () => {
    // Default to true if the property isn't defined
    const acceptBankTransfer = storeSettings?.paymentSettings?.acceptBankTransfer ?? true;
    
    // Online payment is enabled if bank transfer is NOT accepted
    return !acceptBankTransfer;
  };

  // Initialize default delivery zone (like CartSidebar)
  useEffect(() => {
    // Remove auto-selection - user must choose explicitly
    setSelectedDeliveryZone(null);
  }, [storeSettings.shipping?.deliveryZones]);

  const getBookingsTotal = () => {
    return bookings.reduce((total, booking) => total + booking.totalPrice, 0);
  };

  const getBookingsCount = () => {
    return bookings.length;
  };

  // ✅ MATCH CART: Convert bookings to cart format for unified checkout
  const convertBookingsToCartFormat = () => {
    return bookings.map(booking => ({
      id: booking.id,
      name: booking.service.name,
      price: booking.totalPrice, // Total including options
      quantity: 1, // Services always qty 1
      imageUrl: booking.service.image,
      // ✅ Service-specific metadata
      isService: true,
      serviceDetails: {
        duration: booking.service.duration,
        deliveryMethod: booking.service.deliveryMethod,
        selectedOptions: booking.selectedOptions || []
      }
    }));
  };

  // ✅ MATCH CART: Calculate values like CartSidebar
  const subtotal = getBookingsTotal();
  const shipping = selectedDeliveryZone?.fee || 0;
  const tax = storeSettings.tax?.enabled 
    ? subtotal * (storeSettings.tax.rate / 100) 
    : 0;
  const discount = 0; // TODO: Add promo code support
  const grandTotal = subtotal + shipping + tax - discount;

  const handleClearAll = () => {
    onClearBookings?.();
    setShowClearConfirm(false);
  };

  const handleContactClick = () => {
    onContact?.();
    onClose();
  };
  
  const validateCheckout = () => {
    // Check if delivery zones are enabled and configured
    if (storeSettings.shipping?.enabled && 
        storeSettings.shipping?.deliveryZones?.length > 0) {
      
      if (!selectedDeliveryZone) {
        setShowDeliveryError(true);
        // Scroll to delivery zone selector
        setTimeout(() => {
          document.getElementById('delivery-zone-selector')?.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }, 100);
        return false;
      }
    }
    
    setShowDeliveryError(false);
    return true;
  };

  // ✅ MATCH CART: Contact order helpers
  const handleContactOrder = (type) => {
    // Validate delivery zone if enabled
    if (!validateCheckout()) {
      return;
    }

    const bookingsSummary = bookings.map(booking => {
      // ✅ CORRECT: Use option.name which is now guaranteed to exist
      const optionsText = booking.selectedOptions?.length > 0
        ? `\n  Options: ${booking.selectedOptions.map(opt => {
            const optionName = opt.name; // Name is always present now
            const quantity = opt.quantity || 1;
            const priceText = opt.isFree ? ' - Free' : ` - R${opt.price * quantity}`;
            
            return `${optionName}${quantity > 1 ? ` (x${quantity})` : ''}${priceText}`;
          }).join(', ')}`
        : '';
      
      return `${booking.service.name} - R${booking.totalPrice.toFixed(2)}${optionsText}`;
    }).join('\n');
    
    // Calculate totals
    const subtotal = getBookingsTotal();
    const deliveryFee = selectedDeliveryZone?.fee || 0;
    const tax = storeSettings.tax?.enabled 
      ? subtotal * (storeSettings.tax.rate / 100) 
      : 0;
    const total = subtotal + deliveryFee + tax;
    
    // Build delivery zone info
    let deliveryInfo = '';
    if (selectedDeliveryZone) {
      deliveryInfo = `\n\n📍 DELIVERY DETAILS
  Delivery Area: ${selectedDeliveryZone.name}
  Location: ${selectedDeliveryZone.areas}
  Delivery Fee: ${deliveryFee === 0 ? 'FREE' : `R${deliveryFee.toFixed(2)}`}`;
      
      if (selectedDeliveryZone.deliveryTime) {
        deliveryInfo += `\nEstimated Delivery: ${selectedDeliveryZone.deliveryTime}`;
      }
      
      if (selectedDeliveryZone.processingTime) {
        deliveryInfo += `\nProcessing Time: ${selectedDeliveryZone.processingTime}`;
      }
    }
    
    // Build order summary
    let orderSummary = `\n\n💰 ORDER SUMMARY
  Subtotal: R${subtotal.toFixed(2)}`;
    
    if (deliveryFee > 0) {
      orderSummary += `\nDelivery: R${deliveryFee.toFixed(2)}`;
    }
    
    if (tax > 0) {
      orderSummary += `\nTax (${storeSettings.tax.rate}%): R${tax.toFixed(2)}`;
    }
    
    orderSummary += `\n━━━━━━━━━━━━━━━
  TOTAL: R${total.toFixed(2)}`;
    
    // Complete message
    const message = `Hi! I'd like to book the following services:

  📋 SERVICES
  ${bookingsSummary}${deliveryInfo}${orderSummary}

  Looking forward to your confirmation! 😊`;
    
    if (type === 'whatsapp') {
      const phone = storeSettings?.whatsappNumber?.replace(/\D/g, '');
      const encodedMessage = encodeURIComponent(message);
      window.open(`https://wa.me/${phone}?text=${encodedMessage}`, '_blank');
    } else if (type === 'email') {
      const email = storeSettings?.email || '';
      const subject = encodeURIComponent('Service Booking Request');
      const body = encodeURIComponent(message);
      window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-300 z-50 ${
          isOpen ? 'opacity-50 visible' : 'opacity-0 invisible'
        }`}
        onClick={onClose}
      />

      {/* Sidebar - MATCH CART STRUCTURE EXACTLY */}
      <div className={`
        fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl transform transition-transform duration-300 ease-in-out z-50
        ${isOpen ? 'translate-x-0' : 'translate-x-full'}
      `}>
        {/* Use CSS Grid for proper layout (same as Cart) */}
        <div className="h-full grid grid-rows-[auto_1fr_auto]">
          
          {/* Header - Fixed at top */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-gray-700" />
              <h2 className="text-lg font-semibold text-gray-900">
                Your Booking/Order ({getBookingsCount()})
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Main Content Area - Scrollable */}
          <div className="overflow-y-auto min-h-0">
            {bookings.length === 0 ? (
              /* Empty State */
              <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Calendar className="h-10 w-10 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No bookings yet</h3>
                <p className="text-gray-500 mb-6">Browse services and add bookings to get started!</p>
                <button
                  onClick={onClose}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  style={{ backgroundColor: styling.primaryColor || '#3b82f6' }}
                >
                  Browse Services
                </button>
              </div>
            ) : (
              <div className="p-4 space-y-4">
                {/* Booking Items */}
                {bookings.map((booking) => (
                  <BookingItem
                    key={booking.id}
                    booking={booking}
                    onRemove={() => onRemoveBooking?.(booking.id)}
                    styling={styling}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Delivery Zone Selection */}
          {storeSettings.shipping?.enabled && storeSettings.shipping?.deliveryZones?.length > 0 && (
            <div 
              id="delivery-zone-selector"
              className={`border-t border-gray-200 pt-4 pb-4 ${
                showDeliveryError ? 'animate-shake' : ''
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <Truck className="h-4 w-4 text-blue-600" />
                  Select Delivery Area *
                </label>
                {selectedDeliveryZone && (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                )}
              </div>

              {showDeliveryError && (
                <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-600">
                    Please select a delivery area to continue
                  </p>
                </div>
              )}

              <div className="space-y-2">
                {storeSettings.shipping.deliveryZones.map((zone) => (
                  <button
                    key={zone.name}
                    onClick={() => {
                      setSelectedDeliveryZone(zone);
                      setShowDeliveryError(false);
                    }}
                    className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all ${
                      selectedDeliveryZone?.name === zone.name
                        ? 'border-blue-600 bg-blue-50 shadow-sm'
                        : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <MapPin className={`h-4 w-4 ${
                            selectedDeliveryZone?.name === zone.name 
                              ? 'text-blue-600' 
                              : 'text-gray-400'
                          }`} />
                          <span className="font-semibold text-gray-900">{zone.name}</span>
                        </div>
                        
                        <p className="text-sm text-gray-600 mb-2">{zone.areas}</p>
                        
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          {zone.deliveryTime && (
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              <span>{zone.deliveryTime}</span>
                            </div>
                          )}
                          {zone.processingTime && (
                            <div className="flex items-center gap-1">
                              <Package className="h-3 w-3" />
                              <span>Ready in {zone.processingTime}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="text-right flex-shrink-0">
                        <div className={`font-bold ${
                          zone.fee === 0 ? 'text-green-600' : 'text-gray-900'
                        }`}>
                          {zone.fee === 0 ? 'FREE' : `R${zone.fee}`}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {/* Free Shipping Notice */}
              {storeSettings.shipping.freeShippingEnabled && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
                  <Info className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-green-700">
                    {storeSettings.shipping.freeShippingMessage || 
                    `Free shipping on orders over R${storeSettings.shipping.freeShippingThreshold}`}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Footer - Fixed at bottom */}
          {bookings.length > 0 && (
            <div className="border-t border-gray-200 bg-white">
              <div className="p-4 space-y-4">
                {/* Order Summary */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal ({getBookingsCount()} services)</span>
                    <span className="font-medium">R{subtotal.toFixed(2)}</span>
                  </div>
                  
                  {/* Show delivery zone info */}
                  {storeSettings.shipping?.enabled && storeSettings.shipping?.deliveryZones?.length > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 flex items-center gap-1">
                        <Truck className="h-3.5 w-3.5" />
                        Delivery
                        {selectedDeliveryZone && (
                          <span className="text-xs text-gray-500">({selectedDeliveryZone.name})</span>
                        )}
                      </span>
                      <span className={`font-medium ${shipping === 0 ? 'text-green-600' : ''}`}>
                        {selectedDeliveryZone 
                          ? (shipping === 0 ? 'FREE' : `R${shipping.toFixed(2)}`)
                          : 'Select area'
                        }
                      </span>
                    </div>
                  )}
                  
                  {storeSettings.tax?.enabled && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Tax ({storeSettings.tax.rate}%)</span>
                      <span className="font-medium">R{tax.toFixed(2)}</span>
                    </div>
                  )}
                  
                  <div className="border-t border-gray-200 pt-2 flex justify-between font-bold text-lg">
                    <span>Total</span>
                    <span className="text-blue-600">R{grandTotal.toFixed(2)}</span>
                  </div>
                </div>

                {/* Trust Elements (same as Cart) */}
                <div className="flex items-center justify-center gap-4 text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    <Shield className="h-3 w-3" />
                    Secure
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    Confirmed
                  </div>
                  <div className="flex items-center gap-1">
                    <CreditCard className="h-3 w-3" />
                    Easy Payment
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="p-6 border-t bg-gray-50 space-y-3">
                {/* ✅ Show Checkout button ONLY if PayFast is enabled */}
                {isOnlinePaymentEnabled() ? (
                  <button
                    onClick={() => {
                      if (validateCheckout()) {
                        setShowCheckout(true);
                      }
                    }}
                    disabled={bookings.length === 0}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 px-6 rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-3 shadow-lg hover:shadow-xl group"
                    style={{
                      background: bookings.length === 0 
                        ? '#9ca3af' 
                        : `linear-gradient(135deg, ${styling.primaryColor || '#3b82f6'}, ${styling.accentColor || '#2563eb'})`
                    }}
                  >
                    <CreditCard className="h-5 w-5 group-hover:scale-110 transition-transform" />
                    <span>Proceed to Checkout</span>
                    <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                ) : (
                  
                  <div className="space-y-2">
                    {/* WhatsApp Button */}
                    {storeSettings?.whatsappNumber && (
                      <button
                        onClick={() => {
                          if (validateCheckout()) {
                            handleContactOrder('whatsapp');
                          }
                        }}
                        disabled={bookings.length === 0}
                        className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white py-4 px-6 rounded-xl font-semibold hover:from-green-700 hover:to-green-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-3 shadow-lg hover:shadow-xl group"
                      >
                        <MessageSquare className="h-5 w-5 group-hover:scale-110 transition-transform" />
                        <span>Book via WhatsApp</span>
                        <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                      </button>
                    )}

                    {/* Email Button */}
                    {storeSettings?.email && (
                      <button
                        onClick={() => {
                          if (validateCheckout()) {
                            handleContactOrder('email');
                          }
                        }}
                        disabled={bookings.length === 0}
                        className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 px-6 rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-3 shadow-lg hover:shadow-xl group"
                      >
                        <Mail className="h-5 w-5 group-hover:scale-110 transition-transform" />
                        <span>Book via Email</span>
                        <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                      </button>
                    )}
                  </div>
                )}

                {/* Clear All Button */}
                {bookings.length > 0 && (
                  <button
                    onClick={() => setShowClearConfirm(true)}
                    className="w-full py-3 px-6 border-2 border-red-200 text-red-600 rounded-xl font-medium hover:bg-red-50 transition-colors flex items-center justify-center gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    Clear All Bookings
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Clear Confirmation Modal */}
      {showClearConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Clear All Bookings?</h3>
                <p className="text-sm text-gray-600">This will remove all selected services</p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowClearConfirm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleClearAll}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ CHECKOUT MODAL - EXACTLY LIKE CART */}
      {showCheckout && (
        <CheckoutModal
          isOpen={showCheckout}
          onClose={() => setShowCheckout(false)}
          cart={convertBookingsToCartFormat()} // ✅ Convert bookings to cart format
          storeSettings={storeSettings}
          orderSummary={{
            subtotal,
            shipping,
            tax,
            discount,
            grandTotal,
            selectedDeliveryZone,
            appliedPromo: null
          }}
          onOrderComplete={() => {
            onClearBookings(); // Clear bookings instead of cart
            setShowCheckout(false);
            onClose();
          }}
        />
      )}
    </>
  );
};

// Booking Item Component (unchanged)
const BookingItem = ({ booking, onRemove, styling }) => {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex gap-3">
        {/* Service Image */}
        {booking.service.image ? (
          <img
            src={booking.service.image}
            alt={booking.service.name}
            className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
          />
        ) : (
          <div className="w-16 h-16 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg flex items-center justify-center flex-shrink-0">
            <Calendar className="h-8 w-8 text-blue-300" />
          </div>
        )}

        {/* Service Info */}
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-gray-900 text-sm line-clamp-2 mb-1">
            {booking.service.name}
          </h4>
          
          <div className="flex items-center gap-3 text-xs text-gray-600 mb-2">
            {booking.service.duration && (
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                <span>{booking.service.duration}</span>
              </div>
            )}
            {booking.service.deliveryMethod && (
              <div className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                <span className="capitalize">{booking.service.deliveryMethod.replace('-', ' ')}</span>
              </div>
            )}
          </div>

          <div className="text-lg font-bold text-red-600">
            R{booking.totalPrice.toFixed(2)}
          </div>
        </div>

        {/* Remove Button */}
        <button
          onClick={onRemove}
          className="p-2 text-red-600 hover:bg-red-50 rounded-lg h-fit transition-colors"
          title="Remove"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {/* Selected Options */}
      {booking.selectedOptions && booking.selectedOptions.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center justify-between w-full text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <span className="font-medium flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              {booking.selectedOptions.length} option{booking.selectedOptions.length !== 1 ? 's' : ''}
            </span>
            <ChevronRight
              className={`h-4 w-4 transition-transform ${showDetails ? 'rotate-90' : ''}`}
            />
          </button>

          {showDetails && (
            <div className="mt-2 space-y-1.5">
              {booking.selectedOptions.map((option, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between text-xs bg-white border border-gray-200 rounded px-3 py-2"
                >
                  <span className="text-gray-700">
                    {option.name} {option.quantity > 1 && `×${option.quantity}`}
                  </span>
                  {option.isFree ? (
                    <span className="text-green-600 font-semibold">Free</span>
                  ) : (
                    <span className="text-gray-600 font-semibold">
                      +R{(option.price * option.quantity).toFixed(2)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};